import { Alert, CircularProgress } from '@mui/joy';

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AuthButton } from '@/components/Auth';
import { VideoBackgroundPageWrapper } from '@/components/VideoBackgroundPageWrapper';
import { supabase } from '@/utils/backend-auth.tsx';

export default function ConfirmSignup() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);

  // Get token_hash from URL parameters
  const [searchParams] = useSearchParams();
  const tokenHash = searchParams.get('token_hash');

  const handleConfirm = async () => {
    if (!tokenHash || !supabase) {
      setError('Invalid token or Supabase not initialized');
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'email',
      });

      if (error) {
        setError(error.message);
      } else if (data.session) {
        setVerified(true);
        // Verification successful, redirect to onboarding
        navigate('/onboarding');
      } else {
        setError('Verification failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const returnToLogin = () => {
    navigate('/');
  };

  return (
    <VideoBackgroundPageWrapper>
      <div className="flex h-full w-full items-center justify-center">
        <div className="flex w-full max-w-md grow flex-col gap-4 rounded-lg bg-primary p-8 shadow-md">
          <h2 className="mb-3 text-center text-4xl tracking-[-0.06em]">
            Confirm Your Account
          </h2>
          {error && <Alert color="danger">{error}</Alert>}

          <div className="flex flex-col items-center justify-center gap-4 p-4">
            {isLoading || verified ? (
              <div className="flex flex-col items-center gap-4">
                <CircularProgress />
                <p>Verifying your account...</p>
              </div>
            ) : (
              <>
                <p>Would you like to confirm your account?</p>

                <div className="flex w-full max-w-xs flex-col gap-2">
                  <AuthButton
                    type="button"
                    variant="primary"
                    disabled={isLoading}
                    onClick={handleConfirm}
                  >
                    Confirm
                  </AuthButton>

                  <AuthButton
                    type="button"
                    variant="secondary"
                    disabled={isLoading}
                    onClick={returnToLogin}
                  >
                    Return to Login
                  </AuthButton>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </VideoBackgroundPageWrapper>
  );
}
